import React from "react";
import { map, sumBy } from "lodash";
import styled from "styled-components";
import { FontWeight, Spacing, border, form, transparentize } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import Counter from "@ui-kit/format/counter";
import Gauge from "@ui-kit/gauge";
import Tooltip from "@ui-kit/tooltip";
import Pictures from "@radarly/components/image-clustering/pictures";
import { tr } from "@radarly/i18n";
import { ClusterPost } from "@radarly/models/posts";
import GaugeTooltip from "./tooltip";

type GaugeItem = { label: string; color: string; amount: number };

export interface IProps {
  postCount: number;
  tags: string[];
  sentiments: GaugeItem[];
  platforms: GaugeItem[];
  pictures: ClusterPost[];
  display: "row" | "grid";
  onClickMore: () => void;
  locale: Locale;
  renderTooltip: (data: ClusterPost) => React.ReactNode;
}

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.Medium};
  margin-bottom: ${Spacing.Large};

  > * {
    flex-shrink: 0;
  }
`;

const PostCount = styled.div`
  white-space: nowrap;
  font-weight: ${FontWeight.Bold};
`;

const Separator = styled.div`
  height: 16px;
  width: 1px;
  background-color: ${border};
`;

const Pills = styled.div`
  width: 100%;
  display: flex;
  gap: ${Spacing.Medium};
  align-items: center;
  flex-wrap: wrap;
  overflow: hidden;
  height: 32px;
  flex-shrink: 1;
`;

const Pill = styled.div`
  line-height: 32px;
  border-radius: 16px;
  background-color: ${(props) => transparentize(0.85, form(props))};
  color: ${form};
  padding: 0 ${Spacing.Medium};
`;

const StyledGauge = styled(Gauge)`
  width: 200px;
`;

const Gauges = styled.div`
  display: flex;
  gap: ${Spacing.Small};
  align-items: center;
  margin-bottom: ${Spacing.Large};
`;

const TooltipContent = styled.div`
  max-width: 200px;
`;

const RadarlyImageClusteringCluster = ({
  postCount,
  tags,
  sentiments,
  platforms,
  pictures,
  display,
  onClickMore,
  locale,
  renderTooltip,
}: IProps) => {
  const totalAmountSentiments = sumBy(sentiments, "amount");
  const totalAmountPlatforms = sumBy(platforms, "amount");

  return (
    <>
      <Header>
        <PostCount>
          {tr(locale, "IMAGE_CLUSTER::POSTS")}{" "}
          <Counter number={postCount} locale={locale} />
        </PostCount>
        <Separator />
        <Pills>
          {map(tags, (tag, key) => (
            <Tooltip
              tooltip={
                <TooltipContent>
                  {tr(locale, "IMAGE_CLUSTER::TERMS_TOOLTIP")}
                </TooltipContent>
              }
              key={key}
              placement="top"
            >
              <Pill key={key}>{tag}</Pill>
            </Tooltip>
          ))}
        </Pills>
        {/* TODO : add menu when actions are implemented */}
        {/* <Button icon="dots-3-v" size="s" variant="tertiary" /> */}
      </Header>
      <Gauges>
        {tr(locale, "IMAGE_CLUSTER::TONES")}
        <StyledGauge
          items={sentiments}
          tooltip={(index) => (
            <GaugeTooltip
              locale={locale}
              color={sentiments[index].color}
              label={sentiments[index].label}
              amount={sentiments[index].amount}
              total={totalAmountSentiments}
            />
          )}
        />
        <Separator />
        {tr(locale, "IMAGE_CLUSTER::PLATFORMS")}
        <StyledGauge
          items={platforms}
          tooltip={(index) => (
            <GaugeTooltip
              locale={locale}
              color={platforms[index].color}
              label={platforms[index].label}
              amount={platforms[index].amount}
              total={totalAmountPlatforms}
            />
          )}
        />
      </Gauges>

      <Pictures
        pictures={pictures}
        display={display}
        onClickMore={onClickMore}
        renderTooltip={renderTooltip}
      />
    </>
  );
};

export default RadarlyImageClusteringCluster;
